import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Building2, DoorOpen, CalendarDays, Users, RefreshCw,
  Sparkles, Wrench, MessageSquare, DollarSign, FileText, UserCog,
  Settings, LogOut, Menu, X, BarChart3, Download, Wallet, Mail,
  Bell, Shield, CreditCard, User
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

const navSections = [
  {
    title: null,
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'manager', 'cleaner', 'maintenance'] },
    ]
  },
  {
    title: 'Property Management',
    roles: ['admin', 'manager'],
    items: [
      { to: '/properties', icon: Building2, label: 'Properties', roles: ['admin', 'manager'] },
      { to: '/units', icon: DoorOpen, label: 'Units', roles: ['admin', 'manager'] },
      { to: '/guests', icon: Users, label: 'Guests', roles: ['admin', 'manager'] },
      { to: '/reservations', icon: CalendarDays, label: 'Calendar / Reservations', roles: ['admin', 'manager'] },
    ]
  },
  {
    title: 'Operations',
    roles: ['admin', 'manager', 'cleaner', 'maintenance'],
    items: [
      { to: '/cleaning', icon: Sparkles, label: 'Cleaning Jobs', roles: ['admin', 'manager', 'cleaner'] },
      { to: '/maintenance', icon: Wrench, label: 'Maintenance', roles: ['admin', 'manager', 'maintenance'] },
    ]
  },
  {
    title: 'Integrations',
    roles: ['admin', 'manager'],
    items: [
      { to: '/ical', icon: RefreshCw, label: 'iCal Sync', roles: ['admin', 'manager'] },
      { to: '/messages', icon: Mail, label: 'Messaging', roles: ['admin', 'manager'] },
    ]
  },
  {
    title: 'Reports & Analytics',
    roles: ['admin', 'manager'],
    items: [
      { to: '/reports', icon: BarChart3, label: 'Analytics', roles: ['admin', 'manager'] },
      { to: '/financials', icon: DollarSign, label: 'Financial Reports', roles: ['admin'] },
      { to: '/invoices', icon: FileText, label: 'Invoices', roles: ['admin'] },
      { to: '/services-fees', icon: FileText, label: 'Services & Fees', roles: ['admin'] },
    ]
  },
  {
    title: 'Account',
    roles: ['admin'],
    items: [
      { to: '/users', icon: UserCog, label: 'Staff Management', roles: ['admin', 'manager'] },
      { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
    ]
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userRole = user?.role || 'manager';
  const isDeveloper = user?.email === 'owner@rentalhub.app';

  const filteredSections = navSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.roles.includes(userRole))
    }))
    .filter(section => section.items.length > 0);

  // Add Subscriptions link only for developer
  if (isDeveloper) {
    const accountSection = filteredSections.find(s => s.title === 'Account');
    if (accountSection) {
      accountSection.items.push({ to: '/subscriptions', icon: CreditCard, label: 'Subscriptions', roles: ['admin'] });
    }
  }

  return (
    <div className="flex h-screen bg-slate-50" data-testid="app-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex items-center h-44 px-3 border-b border-slate-200 overflow-hidden">
          <div className="relative" data-testid="app-logo">
            <img src="https://customer-assets.emergentagent.com/job_mongo-property-app/artifacts/xfew4ztu_06B377E7-711C-4306-B7DD-5FE7F88F775D.png" alt="RentalHub" className="w-full object-contain max-h-40" />
            <span className="absolute text-[8px] font-semibold text-slate-400 select-none" style={{ top: '38%', right: '15%' }}>TM</span>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto" data-testid="sidebar-nav">
          {filteredSections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-4' : ''}>
              {section.title && (
                <p className="px-3 mb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{section.title}</p>
              )}
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : 'text-slate-600'}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 shrink-0">
          <button className="lg:hidden mr-4" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar-btn">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span className="hidden sm:block">{user?.email}</span>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
